define('presentation/settings/liveReceptionistScripts/facades/addLiveReceptionistScriptFacade',[
    'businessServices/converters/textConverter',
    'common/collections/collectionSorter',
    'common/collections/enumerationKeyValueConverter',
    'common/storage/commonState',
    'constants/integrationsConstants',
    'constants/userGroupConstants',
    'externalDependencies/clientWebSocket',
    'persistence/webSocket/webSocketApp',
    'presentation/common/dateTimeValue',
    'presentation/settings/liveReceptionistScripts/presentationObjects/liveReceptionistScriptsPresentationObject',
    'presentation/settings/liveReceptionistScripts/presentationObjects/liveReceptionistSchedulePresentationObject',
    'presentation/settings/liveReceptionistScripts/presentationObjects/liveReceptionistScheduleSegmentPresentationObject',
    'settings/navigationConfiguration'
], function () {

    const SorterConstructor = require('common/collections/collectionSorter');
    let _sorter = new SorterConstructor();

    const TextConverterConstructor = require('businessServices/converters/textConverter');
    const _textConverter = new TextConverterConstructor();

    const DateTimeValueConstructor = require('presentation/common/dateTimeValue');
    const LiveReceptionistScriptsPresentationObjectConstructor = require('presentation/settings/liveReceptionistScripts/presentationObjects/liveReceptionistScriptsPresentationObject');
    const LiveReceptionistSchedulePresentationObjectConstructor = require('presentation/settings/liveReceptionistScripts/presentationObjects/liveReceptionistSchedulePresentationObject');
    const LiveReceptionistScheduleSegmentPresentationObjectConstructor = require('presentation/settings/liveReceptionistScripts/presentationObjects/liveReceptionistScheduleSegmentPresentationObject');

    const clientWebSocket = require('externalDependencies/clientWebSocket');
    const liveReceptionistScriptSocket = clientWebSocket.forApplication("liveReceptionistScript");
    const integrationsSocket = clientWebSocket.forApplication("integrations");

    const _commonState = require('common/storage/commonState');
    const _i18n = require('i18next');
    const _integrationsConstants = require('constants/integrationsConstants');
    const _keyValueConverter = require('common/collections/enumerationKeyValueConverter');
    const _navigationConfiguration = require('settings/navigationConfiguration');
    const _userGroupConstants = require('constants/userGroupConstants');

    let _promiseFactory = null;
    let _webSocketApp = null;

    const mapIntegrations = (integrations) => {
        let selectedIntegrations = integrations.map(integration => {
            return mapIntegration(integration);
        });
        _sorter.sort(selectedIntegrations, "name", true);
        return selectedIntegrations;
    };

    const mapIntegration = (integration) => {
        const integrationsConstants = _keyValueConverter.convertToKeyValues(_integrationsConstants);
        const foundIntegration = integrationsConstants.find((item) => item.value === integration);
        const integrationKey = _textConverter.camelCase(foundIntegration.key);
        
        return {
            iconCss: `add-integration__icon--${integrationKey}`,
            id: foundIntegration.value.toString(),
            name: _i18n.t("integrations:integrationName", {context: integrationKey}),
            type: "integration",
            value: foundIntegration.value
        };
    };

    const mapSubscribers = (subscribers) => {
        let selectedSubscribers = subscribers.map(subscriber => {
            return mapSubscriber(subscriber);
        });
        _sorter.sort(selectedSubscribers, "displayName", true);
        return selectedSubscribers;
    };

    const mapSubscriber = (subscriber) => {
        const commonStateItem = subscriber.userId ? _commonState.get(subscriber.userId) : _commonState.get(subscriber.userGroupId);
        const avatar = commonStateItem.type === "user" ? {avatar: commonStateItem.avatar()} : {};
        const liveReceptionistScriptSubscriber = subscriber.liveReceptionistScriptSubscriberId ? {liveReceptionistScriptSubscriberId: subscriber.liveReceptionistScriptSubscriberId} : {};
        return Object.assign({}, {
                id: commonStateItem.id,
                subscriberType: commonStateItem.type,
                displayName: commonStateItem.name()
            },
            avatar,
            liveReceptionistScriptSubscriber
        );
    };

    const _processScheduleForDisplay = (liveReceptionistScriptId, schedule) => {
        if (schedule === null) {
            return null;
        }

        const displaySchedule = new LiveReceptionistSchedulePresentationObjectConstructor();
        displaySchedule.accountAttendantScheduleId = schedule.accountAttendantScheduleId;
        displaySchedule.liveReceptionistScriptId = liveReceptionistScriptId;
        displaySchedule.name = schedule.name;
        displaySchedule.createdDateTime = new DateTimeValueConstructor(schedule.createdDateTime);
        displaySchedule.modifiedDateTime = new DateTimeValueConstructor(schedule.updatedDateTime);
        displaySchedule.editScheduleUrl = _navigationConfiguration.routesById.editSchedule.baseUrl + schedule.accountAttendantScheduleId;
        displaySchedule.segments = schedule.segments.map((currentSegment) => {
            const segment = new LiveReceptionistScheduleSegmentPresentationObjectConstructor();
            segment.scheduleId = schedule.accountAttendantScheduleId;
            segment.endHour(currentSegment.endHour);
            segment.endMinute(currentSegment.endMinute);
            segment.isForSunday(currentSegment.isForSunday);
            segment.isForMonday(currentSegment.isForMonday);
            segment.isForTuesday(currentSegment.isForTuesday);
            segment.isForWednesday(currentSegment.isForWednesday);
            segment.isForThursday(currentSegment.isForThursday);
            segment.isForFriday(currentSegment.isForFriday);
            segment.isForSaturday(currentSegment.isForSaturday);
            segment.sequence(currentSegment.sequence);
            segment.startHour(currentSegment.startHour);
            segment.startMinute(currentSegment.startMinute);
            return segment;
        });

        return displaySchedule;
    };

    const _processIntegrationsForDisplay = (integrations) => {
        if (integrations.length > 0) {
            return mapIntegrations(integrations);
        }
        
        return [];
    };

    const _processSubscribersForDisplay = (subscribers) => {
        if (subscribers.length > 0) {
            return mapSubscribers(subscribers);
        }

        const defaultSubscriber = _commonState.resolveGroupIdToGuid(_userGroupConstants.allUserGroupId);
        return [mapSubscriber({userGroupId: defaultSubscriber})];
    };

    const _getLiveReceptionistScript = (liveReceptionistScriptId) => {
        return _promiseFactory.defer((promise) => {
            const parameters = {
                liveReceptionistScriptId: liveReceptionistScriptId
            };
            liveReceptionistScriptSocket.send("getLiveReceptionistScript", parameters, (response) => {
                if (response.isFound === false) {
                    promise.reject("Not Found");
                    return;
                }
                const liveReceptionistScriptPresentationObject = new LiveReceptionistScriptsPresentationObjectConstructor();
                liveReceptionistScriptPresentationObject.liveReceptionistScriptId = liveReceptionistScriptId;
                liveReceptionistScriptPresentationObject.liveReceptionistPortalScriptId = response.liveReceptionistPortalScriptId;
                liveReceptionistScriptPresentationObject.liveReceptionistPortalScriptIntegrationsId = response.liveReceptionistPortalScriptIntegrationsId;
                liveReceptionistScriptPresentationObject.LiveReceptionistPortalScriptUrl = response.liveReceptionistPortalScriptUrl;
                liveReceptionistScriptPresentationObject.liveReceptionistScriptName = response.displayName;
                liveReceptionistScriptPresentationObject.liveReceptionistQueueId = response.queueId;
                liveReceptionistScriptPresentationObject.liveReceptionistQueueHoldSetting = response.queueHoldSetting.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistWarmTransferCallerId = response.warmTransferCallerId.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistManualEntryTransferOptions = response.manualEntryTransferOptions.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistUserTransferOptions = response.userTransferOptions.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistUserTransferSelectionType = response.userTransferSelectionType.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistSelectedUserIds = response.users;
                liveReceptionistScriptPresentationObject.liveReceptionistUserGroupTransferOptions = response.userGroupTransferOptions.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistUserGroupTransferSelectionType = response.userGroupTransferSelectionType.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistSelectedUserGroupIds = response.userGroups;
                liveReceptionistScriptPresentationObject.liveReceptionistForwardingNumberTransferOptions = response.forwardingNumberTransferOptions.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistForwardingNumberTransferSelectionType = response.forwardingNumberTransferSelectionType.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistSelectedForwardingNumberIds = response.forwardingNumbers;
                liveReceptionistScriptPresentationObject.liveReceptionistVoicemailBoxTransferOptions = response.voicemailBoxTransferOptions.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistVoicemailBoxTransferSelectionType = response.voicemailBoxTransferSelectionType.toString();
                liveReceptionistScriptPresentationObject.liveReceptionistSelectedVoicemailBoxIds = response.voicemailBoxes;
                liveReceptionistScriptPresentationObject.status = response.status;
                liveReceptionistScriptPresentationObject.type = response.type.toString();
                liveReceptionistScriptPresentationObject.schedule = _processScheduleForDisplay(liveReceptionistScriptId, response.schedule);
                liveReceptionistScriptPresentationObject.subscribers = _processSubscribersForDisplay(response.subscribers);
                liveReceptionistScriptPresentationObject.integrations = _processIntegrationsForDisplay(response.integrations);
                promise.resolve(liveReceptionistScriptPresentationObject);
            });
        });
    };

    const _getLiveReceptionistScriptTemplates = () => {
        return _promiseFactory.defer((promise) => {
            liveReceptionistScriptSocket.send("retrieveAllTemplates", {}, (response) => {
                promise.resolve(response);
            });
        });
    };

    const _getConnectedIntegrations = () => {
        return _promiseFactory.defer((promise) => {
            integrationsSocket.send("retrieve", {}, (response) => {
                const connectedIntegrations = response.filter(i => i.isConnected).map(i => i.platformId);
                const displayIntegrations = _processIntegrationsForDisplay(connectedIntegrations);
                promise.resolve(displayIntegrations);
            });
        });
    };

    const _addLiveReceptionistScript = (
        name,
        type,
        queueId,
        queueHoldSetting,
        warmTransferCallerId,
        manualEntryTransferOptions,
        selectedUserTransferOption,
        selectedUserTransferSelectionType,
        selectedUsers,
        selectedUserGroupTransferOption,
        selectedUserGroupTransferSelectionType,
        selectedUserGroups,
        selectedForwardingNumberTransferOption,
        selectedForwardingNumberTransferSelectionType,
        selectedForwardingNumbers,
        selectedVoicemailBoxTransferOption,
        selectedVoicemailBoxTransferSelectionType,
        selectedVoicemailBoxes,
        selectedIntegrations,
        selectedTemplateId,
        selectedSubscribers
    ) => {
        const request = {
            displayName: name,
            type: parseInt(type),
            queueId: queueId,
            queueHoldSetting: parseInt(queueHoldSetting),
            warmTransferCallerId: parseInt(warmTransferCallerId),
            manualEntryTransferOptions: parseInt(manualEntryTransferOptions),
            userTransferOptions: parseInt(selectedUserTransferOption),
            userTransferSelectionType: parseInt(selectedUserTransferSelectionType),
            users: selectedUsers,
            userGroupTransferOptions: parseInt(selectedUserGroupTransferOption),
            userGroupTransferSelectionType: parseInt(selectedUserGroupTransferSelectionType),
            userGroups: selectedUserGroups,
            forwardingNumberTransferOptions: parseInt(selectedForwardingNumberTransferOption),
            forwardingNumberTransferSelectionType: parseInt(selectedForwardingNumberTransferSelectionType),
            forwardingNumbers: selectedForwardingNumbers,
            voicemailBoxTransferOptions: parseInt(selectedVoicemailBoxTransferOption),
            voicemailBoxTransferSelectionType: parseInt(selectedVoicemailBoxTransferSelectionType),
            voicemailBoxes: selectedVoicemailBoxes,
            integrations: selectedIntegrations,
            templateId: selectedTemplateId,
            subscribers: selectedSubscribers
        };

        return _promiseFactory.defer((promise) => {
            liveReceptionistScriptSocket.send("addLiveReceptionistScript", request, (response) => {
                if (response.isSuccessful === false) {
                    promise.reject();
                    return;
                }
                const { liveReceptionistScriptId, liveReceptionistPortalScriptId, displayName } = response;
                promise.resolve({liveReceptionistScriptId, liveReceptionistPortalScriptId, liveReceptionistScriptName: displayName});
            });
        });
    };

    const _updateLiveReceptionistScript = (
        liveReceptionistScriptId,
        liveReceptionistPortalScriptIntegrationsId,
        name,
        type,
        queueId,
        queueHoldSetting,
        warmTransferCallerId,
        manualEntryTransferOptions,
        selectedUserTransferOption,
        selectedUserTransferSelectionType,
        selectedUsers,
        selectedUserGroupTransferOption,
        selectedUserGroupTransferSelectionType,
        selectedUserGroups,
        selectedForwardingNumberTransferOption,
        selectedForwardingNumberTransferSelectionType,
        selectedForwardingNumbers,
        selectedVoicemailBoxTransferOption,
        selectedVoicemailBoxTransferSelectionType,
        selectedVoicemailBoxes,
        selectedIntegrations,
        selectedSubscribers
    ) => {
        const request = {
            liveReceptionistScriptId: liveReceptionistScriptId,
            liveReceptionistPortalScriptIntegrationsId: liveReceptionistPortalScriptIntegrationsId,
            displayName: name,
            type: parseInt(type),
            queueId: queueId,
            queueHoldSetting: parseInt(queueHoldSetting),
            warmTransferCallerId: parseInt(warmTransferCallerId),
            manualEntryTransferOptions: parseInt(manualEntryTransferOptions),
            userTransferOptions: parseInt(selectedUserTransferOption),
            userTransferSelectionType: parseInt(selectedUserTransferSelectionType),
            users: selectedUsers,
            userGroupTransferOptions: parseInt(selectedUserGroupTransferOption),
            userGroupTransferSelectionType: parseInt(selectedUserGroupTransferSelectionType),
            userGroups: selectedUserGroups,
            forwardingNumberTransferOptions: parseInt(selectedForwardingNumberTransferOption),
            forwardingNumberTransferSelectionType: parseInt(selectedForwardingNumberTransferSelectionType),
            forwardingNumbers: selectedForwardingNumbers,
            voicemailBoxTransferOptions: parseInt(selectedVoicemailBoxTransferOption),
            voicemailBoxTransferSelectionType: parseInt(selectedVoicemailBoxTransferSelectionType),
            voicemailBoxes: selectedVoicemailBoxes,
            integrations: selectedIntegrations,
            subscribers: selectedSubscribers
        };
        return _promiseFactory.defer((promise) => {
            liveReceptionistScriptSocket.send("updateLiveReceptionistScript", request, (response) => {
                if (response.isSuccessful === false) {
                    promise.reject();
                    return;
                }
                promise.resolve();
            });
        });
    };

    const _sendScriptIntegrationsChangedAutoNotation = (liveReceptionistScriptId, liveReceptionistPortalScriptId, addedIntegrations, removedIntegrations) => {
        return _promiseFactory.defer((promise) => {
            const request = {
                liveReceptionistScriptId,
                liveReceptionistPortalScriptId,
                addedIntegrations,
                removedIntegrations
            };
            _webSocketApp.send("sendScriptIntegrationsChangedAutoNotation", request, (result) => {
                if (result.status === "error") {
                    promise.reject(new Error(result.errorMessage));
                } else {
                    promise.resolve(result);
                }
            });
        });
    };

    const _init = (promiseFactory) => {
        _promiseFactory = promiseFactory;

        const WebSocketAppConstructor = require('persistence/webSocket/webSocketApp');
        _webSocketApp = new WebSocketAppConstructor();
        _webSocketApp.init("live_receptionist_script_edit");
    };

    return function () {
        const self = this;
        self.init = _init;
        self.addLiveReceptionistScript = _addLiveReceptionistScript;
        self.updateLiveReceptionistScript = _updateLiveReceptionistScript;
        self.getConnectedIntegrations = _getConnectedIntegrations;
        self.getLiveReceptionistScript = _getLiveReceptionistScript;
        self.getLiveReceptionistScriptTemplates = _getLiveReceptionistScriptTemplates;
        self.sendScriptIntegrationsChangedAutoNotation = _sendScriptIntegrationsChangedAutoNotation;
    };
});

